// NOTE (Noah, 2026-07-20): Shared detection + reload-loop guard for the
// stale-chunk failure that hits after a deploy when an already-open tab still
// references content-hashed lazy chunks that no longer exist
// (EDITOR-FRONTEND-7YS). Lives in replo-utils so the editor
// boundary, MiniAppShell, and MiniappErrorComponent share one signature.

const CHUNK_LOAD_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "is not a valid javascript mime type",
  "expected a javascript module script",
  "unable to preload css",
];

export const CHUNK_LOAD_RELOADS_AT_KEY = "replo.chunkLoadReloadsAt";

// Attempt n waits DELAYS[n]: a frame so a loader paints, then long enough to outlast a deploy cutover; older attempts are forgotten.
export const CHUNK_LOAD_RELOAD_DELAYS_MS = [150, 2_000, 4_000];
export const CHUNK_LOAD_RELOAD_WINDOW_MS = 60_000;

let reloadScheduled = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  if (error instanceof Error && error.name === "ChunkLoadError") {
    return true;
  }
  const message = (
    error instanceof Error ? error.message : String(error)
  ).toLowerCase();
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

function readRecentReloadsAt(now: number): number[] {
  const parsed: unknown = JSON.parse(
    window.sessionStorage.getItem(CHUNK_LOAD_RELOADS_AT_KEY) ?? "[]",
  );
  return Array.isArray(parsed)
    ? parsed.filter((at): at is number => {
        return (
          typeof at === "number" &&
          now - at >= 0 &&
          now - at < CHUNK_LOAD_RELOAD_WINDOW_MS
        );
      })
    : [];
}

// False without sessionStorage: reloads could not be counted, so the crash UI wins over a possible loop.
export function canReloadForChunkLoad(): boolean {
  try {
    return (
      reloadScheduled ||
      readRecentReloadsAt(Date.now()).length <
        CHUNK_LOAD_RELOAD_DELAYS_MS.length
    );
  } catch {
    return false;
  }
}

// Records one attempt per page and reloads after its delay; false once the budget is spent.
export function scheduleChunkLoadReload(): boolean {
  if (reloadScheduled) {
    return true;
  }
  try {
    const now = Date.now();
    const reloadsAt = readRecentReloadsAt(now);
    const delayMs = CHUNK_LOAD_RELOAD_DELAYS_MS[reloadsAt.length];
    if (delayMs === undefined) {
      return false;
    }
    window.sessionStorage.setItem(
      CHUNK_LOAD_RELOADS_AT_KEY,
      JSON.stringify([...reloadsAt, now]),
    );
    reloadScheduled = true;
    window.setTimeout(() => window.location.reload(), delayMs);
    return true;
  } catch {
    return false;
  }
}

// Preventing the preload error resolves a failed import as undefined. Preserve
// the rejection so chunk boundaries can show their loader until the reload.
export function installVitePreloadErrorReload() {
  window.addEventListener("vite:preloadError", () => {
    scheduleChunkLoadReload();
  });
}

// Inline copy of scheduleChunkLoadReload for when the entry script itself fails to load, since nothing in the bundle runs then.
export const ENTRY_MODULE_RELOAD_HEAD_TAG = {
  tag: "script",
  injectTo: "head",
  children: `(() => {
  let scheduled = false;
  addEventListener("error", (event) => {
    const script = event.target;
    if (scheduled || !(script instanceof HTMLScriptElement) || script.type !== "module") return;
    try {
      const now = Date.now();
      const reloadsAt = JSON.parse(sessionStorage.getItem("${CHUNK_LOAD_RELOADS_AT_KEY}") ?? "[]").filter((at) => now - at >= 0 && now - at < ${CHUNK_LOAD_RELOAD_WINDOW_MS});
      const delayMs = ${JSON.stringify(CHUNK_LOAD_RELOAD_DELAYS_MS)}[reloadsAt.length];
      if (delayMs === undefined) return;
      sessionStorage.setItem("${CHUNK_LOAD_RELOADS_AT_KEY}", JSON.stringify([...reloadsAt, now]));
      scheduled = true;
      setTimeout(() => location.reload(), delayMs);
    } catch {}
  }, true);
})();`,
} as const;
